import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import usePageTracking from "./hooks/usePageTracking";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import CustomerDashboard from "./pages/CustomerDashboard";
import MechanicDashboard from "./pages/MechanicDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import MechanicsListPage from "./pages/MechanicsListPage";
import MechanicDetailPage from "./pages/MechanicDetailPage";
import BookingPage from "./pages/BookingPage";
import MechanicApplicationPage from "./pages/MechanicApplicationPage";
import InvoicePage from "./pages/InvoicePage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import VettingProcessPage from "./pages/VettingProcessPage";
import PoliciesPage from "./pages/PoliciesPage";
import PolicyDetailPage from "./pages/PolicyDetailPage";
import CareersPage from "./pages/CareersPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "mechanic") return <Navigate to="/mechanic" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  usePageTracking();

  // CRITICAL: Check for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with auth checking
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/mechanics" element={<MechanicsListPage />} />
      <Route path="/mechanics/:mechanicId" element={<MechanicDetailPage />} />
      <Route path="/apply" element={<MechanicApplicationPage />} />
      <Route path="/vetting" element={<VettingProcessPage />} />
      <Route path="/careers" element={<CareersPage />} />

      {/* Customer Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["customer", "mechanic", "admin"]}>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/book/:mechanicId"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <BookingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice/:invoiceId"
        element={
          <ProtectedRoute allowedRoles={["customer", "mechanic", "admin"]}>
            <InvoicePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />

      {/* Mechanic Routes */}
      <Route
        path="/mechanic"
        element={
          <ProtectedRoute allowedRoles={["mechanic"]}>
            <MechanicDashboard />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Policy Pages */}
      <Route path="/policies" element={<PoliciesPage />} />
      <Route path="/policies/:policyId" element={<PolicyDetailPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
